<template>
  <div>
    <h2 class="mb-2">{{ $t('clinic_prefs_edit.notifications') }}</h2>

    <!-- Notification Email Settings -->
    <v-card outlined class="pa-4 mb-4">
      <v-card-title class="px-0 py-2">
        <h4>{{ $t('clinic_prefs_edit.notification_email_settings') }}</h4>
      </v-card-title>
      <v-card-text class="px-0 py-2">
        <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.notification_email_help') }}</p>

        <!-- Email Autocomplete with Chips -->
        <v-combobox
          v-model="email_list"
          :items="email_items"
          :label="$t('clinic_prefs_edit.notification_email')"
          :placeholder="$t('clinic_prefs_edit.notification_email_placeholder')"
          outlined prepend-icon="mdi-email"
          chips
          multiple
          deletable-chips
          hide-selected
          :error-messages="email_error"
          @change="on_emails_changed"
          @blur="validate_emails"
        >
          <template v-slot:selection="data">
            <v-chip :key="data.index" :color="is_valid_email(data.item) ? 'grey' : 'error'" :closable="true"
              @click:close="remove_email(data.index)">
              <v-icon left small>mdi-email</v-icon>
              {{ data.item }}
            </v-chip>
          </template>
        </v-combobox>

        <!-- Error Display -->
        <div v-if="email_error" class="mt-2">
          <v-alert outlined type="error" :value="true" dense>
            {{ email_error }}
          </v-alert>
        </div>
      </v-card-text>
    </v-card>

    <!-- Notification Preferences -->
    <v-card outlined class="pa-4 mb-4">
      <v-card-title class="px-0 py-2">
        <h4>{{ $t('clinic_prefs_edit.notification_preferences') }}</h4>
      </v-card-title>
      <v-card-text class="px-0 py-2">
        <p class="text-caption text--secondary mb-4">{{ $t('clinic_prefs_edit.notification_preferences_help') }}</p>

        <v-layout>
            <v-checkbox
              v-model="local_prefs.notifications.user_signup_from_token"
              :label="$t('clinic_prefs_edit.notify_user_signup_from_token')"
              :hint="$t('clinic_prefs_edit.notify_user_signup_from_token_help')"
              persistent-hint
            />
        </v-layout>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ClinicPrefsEditNotifications',
  props: {
    value: {
      type: Object,
      required: true
    },
    clinic_id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      email_error: '',
      loading_emails: false,
      available_emails: []
    };
  },
  computed: {
    local_prefs: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    email_list: {
      get() {
        if (!this.local_prefs.notification_email) {
          return [];
        }
        // Split on commas, trimming whitespace around each email
        return this.local_prefs.notification_email
          .split(/\s*,\s*/)
          .map(email => email.trim())
          .filter(email => email.length > 0);
      },
      set(value) {
        this.update_notification_email(value);
      }
    },
    email_items() {
      return this.available_emails;
    }
  },
  watch: {
    value: {
      handler(newValue) {
        // Ensure notifications object exists and has default values
        if (newValue && !newValue.notifications) {
          this.$set(newValue, 'notifications', {});
        }
        if (newValue && newValue.notifications && newValue.notifications.user_signup_from_token === undefined) {
          this.$set(newValue.notifications, 'user_signup_from_token', false);
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.load_clinic_emails();
  },
  methods: {
    load_clinic_emails() {
      this.loading_emails = true;
      axios.get(`/api/clinic/${this.clinic_id}/users?archived=false&roles=admin,staff`)
        .then((response) => {
          this.loading_emails = false;
          if (response.data.status === 'success') {
            // Extract emails from the response
            this.available_emails = response.data.data
              .map(user => user.email)
              .filter(email => email && email.trim().length > 0);
          } else {
            console.error('Failed to load clinic emails:', response.data.error_message);
          }
        })
        .catch((error) => {
          this.loading_emails = false;
          console.error('Error loading clinic emails:', error);
        });
    },
    is_valid_email(email) {
      const email_regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email_regex.test(email);
    },
    validate_emails() {
      const invalid_emails = this.email_list.filter(email => !this.is_valid_email(email));

      if (invalid_emails.length > 0) {
        this.email_error = this.$t('clinic_prefs_edit.invalid_email');
        return false;
      }

      // Check for duplicates
      const unique_emails = [...new Set(this.email_list)];
      if (unique_emails.length !== this.email_list.length) {
        this.email_error = this.$t('clinic_prefs_edit.duplicate_email');
        return false;
      }

      this.email_error = '';
      return true;
    },
    on_emails_changed(emails) {
      // Filter out any empty strings that might get added
      const filtered_emails = emails.filter(email => email && email.trim().length > 0);
      this.update_notification_email(filtered_emails);
    },
    update_notification_email(emails) {
      // Update the notification_email field with comma-separated string
      const email_string = emails.join(', ');
      this.$set(this.local_prefs, 'notification_email', email_string);

      // Clear any previous errors
      this.email_error = '';
    },
    remove_email(index) {
      const current_emails = [...this.email_list];
      current_emails.splice(index, 1);
      this.update_notification_email(current_emails);
    }
  }
};
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>
